import { computed, h, onMounted, onUpdated, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { QIcon } from 'quasar'
import * as emailValidator from 'email-validator'

import { slot } from './utils'
import { childComponents } from './childComponents'
import { translate } from '../../composables/translator'

/**
 * The UI kit's shared behaviour, out of `CommonMixin`: validation, auto-save, and the render helpers that
 * push both into whatever Quasar component is underneath.
 *
 * Every component in the bundle took all of it by inheritance. Most take only the render helpers — a chip
 * or a tooltip has nothing to validate — and for those the validation state simply never changes, which is
 * why they can call this and use the half they need.
 *
 * `tkey` comes from `I18nMixin`, which existed only to declare that one prop.
 */

/**
 * Where a component is in the validate / auto-save cycle. Lived in the base mixin, which is where every
 * component read it from; it belongs with the state machine that drives it.
 */
export const enum ComponentState {
  Valid,
  Invalid,
  AutoSaving,
  AutoSaved,
  Resetting
}

/**
 * `$attrs` minus the test hooks, for the bags a bundle component spreads into the control it renders.
 *
 * Vue 2 had two bags: `props:` took only what the target declared (so `data-cy` was silently dropped)
 * and `attrs:` was rendered. Vue 3 has ONE flat bag, so re-spreading `$attrs` into it renders every
 * `data-*` attribute on the inner control -- while attribute fallthrough puts the SAME attribute on the
 * component's root element. Two nodes then answer `[data-cy$="sortBy"]`, and Cypress refuses to click:
 * "can only be called on a single element. Your subject contained 2 elements."
 *
 * So the test hooks are left to fallthrough, which lands them on the root exactly once -- where Vue 2
 * put them. Both `$attrs`-derived spreads are filtered: `getRenderProps`'s copy, and `propsPassedToUs`,
 * which reads the raw vnode props bag the parent created and carries the same attributes.
 *
 * What a component passes to `getAttributes` explicitly is NOT filtered -- BsModal's
 * `'data-model': 'btnModalOk'` and the stepper's action buttons mean theirs to be on the control. Nor is
 * `data-model` from the `dataModel` prop: a declared prop never appears in `$attrs`.
 */
const withoutTestHooks = (attrs: Record<string, any>): Record<string, any> => {
  const kept: Record<string, any> = {}

  for (const key of Object.keys(attrs)) {
    if (!key.startsWith('data-')) {
      kept[key] = attrs[key]
    }
  }

  return kept
}

/** The props every component in the bundle accepts. Spread into a component's own `props`. */
export const bsComponentProps = {
  /** A save to run when the field changes; its absence is what turns the whole auto-save path off. */
  autoSave: { type: Function, default: void 0 },
  triggerOnChange: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  validEmail: { type: Boolean, default: false },
  validation: {
    type: Array as unknown as () => ValidationRule[],
    default: void 0
  },
  /**
   * Quasar-shaped rules — `true` to pass, a message to fail — as `:rules` on a QInput would take.
   *
   * Declared here so they gate the auto-save. They used to fall through to the Quasar component as an
   * undeclared attribute, which meant nothing in this component knew whether they had passed: the
   * auto-save gate only ever consulted `validation` (this bundle's own shape), so a value the rules
   * rejected was saved anyway. The author-side book form passes the store-link regexes this way, and
   * typing a single character into "Link to your book on Kobo" persisted it. [sc-28161]
   */
  rules: {
    type: Array as unknown as () => QuasarValidationRule[],
    default: void 0
  },
  /** An element to write the error into instead of rendering the error slot. */
  validationResultTarget: { type: null as unknown as () => any, default: void 0 },
  lazyValidation: { type: Boolean, default: false },
  /** Translation key for the label — was `I18nMixin`'s only member. */
  tkey: { type: String, default: void 0 },
  /**
   * The `data-model` attribute to put on the rendered control, for DOM querying from tests.
   *
   * Explicit now. Vue 2 derived it for free from `$vnode.data.model.expression`, the compiler's
   * internal record of what `v-model` was bound to -- and Vue 3 keeps no such record at runtime, so
   * there is nothing to read. Callers that tests select on pass it themselves; the alternative was a
   * custom compiler transform to reinstate an attribute used by 7 e2e selectors and 24 unit ones.
   */
  dataModel: { type: String, default: void 0 }
}

/**
 * A rule returns nothing when the value passes, and a message when it fails. `ref` is another component
 * to flag alongside this one — "passwords must match" marks both fields.
 *
 * `ref` is `any` rather than `Vue`: the components it points at are class instances, converted
 * components and Quasar components alike, and only `componentState`/`errorMessage` are ever touched.
 */
export type ValidationRule = (val: any) => { ref?: any, label: string } | undefined

/**
 * Quasar's own rule shape, which several screens already write: `true` (or any non-string, non-false
 * value) passes, a string is the message to show. Kept separate from `ValidationRule` because the two
 * report failure in opposite directions and conflating them silently inverts a rule.
 */
export type QuasarValidationRule = (val: any) => boolean | string

export interface BsComponentProps {
  /**
   * Declared as the bare `Function` the prop is, so a component's resolved props assign cleanly. It is
   * called with no arguments and expected to return a promise.
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  autoSave?: Function
  triggerOnChange?: boolean
  required?: boolean
  validEmail?: boolean
  validation?: ValidationRule[]
  /** Quasar's shape; see the prop declaration for why this component has to know about them. */
  rules?: QuasarValidationRule[]
  validationResultTarget?: any
  lazyValidation?: boolean
  tkey?: string
  /** The `data-model` attribute to stamp on the rendered control — see `bsComponentProps`. */
  dataModel?: string
}

/**
 * What a `$refs` entry holding a bundle component is, for the screens that reach into one to show their
 * own saved message. Vue 3 types `$refs` entries loosely and a converted component's instance type is
 * structural rather than a class, so casting a ref to the component itself no longer overlaps — this
 * names the members callers actually touch. `ComponentPublicInstance` replaces the `Vue` type, which
 * Vue 3 does not export.
 */
export type BsComponentRef = ComponentPublicInstance & {
  autoSaveMessage: string
  errorMessage: string
  componentState: ComponentState
  validate: (val?: any) => boolean
  reset: () => void
}

export interface BsComponentContext {
  /** The consuming component's instance: `$attrs`, `$emit`, `$tc`, `$refs` and its own vnode. */
  vm: any
  props: BsComponentProps
  emit: (event: string, ...args: unknown[]) => void

  /**
   * The component's `value` prop, if it declares one. `componentValue` falls back to `$attrs.value`, which
   * is where it lands for the components that do not.
   */
  value?: () => any

  /**
   * The event auto-save listens for. 'blur' unless a component says otherwise — BsSelect and friends save
   * on 'input', because there is no typing to wait for.
   */
  autoSaveMethod?: string

  /** What counts as filled. Overridden where an empty value is meaningful (a false checkbox, a 0 rating). */
  isComponentValid?: (val?: any) => boolean

  /** Runs on the auto-save event before validation — BsChipBox commits its pending chip here. */
  doBeforeAutoSave?: (val: any) => void

  /**
   * Save on every event, and take the value from the event rather than from the model.
   *
   * For a checkbox and a chip box the event IS the change — a click, a chip — so the "has anything
   * actually happened" check has nothing useful to compare and the emitted value is the authoritative
   * one. This used to be decided by looking for `$refs.bsCheckbox` / `$refs.bsChipBox`, with a comment
   * explaining that the component NAME could not be used because the production build mangles it.
   * Vue 3 broke the replacement too: both components hold their child in a setup ref now (a string
   * `ref` in a render function never reaches `$refs`), so the look-up found nothing and every chip box
   * quietly stopped auto-saving. Stated as an option, it cannot go quiet again.
   */
  autoSaveAlways?: boolean

  /**
   * Keep the auto-save handler off while the component shows an error.
   *
   * Only for a component whose own rules mark it invalid where `validate()` cannot see — BsChipBox's
   * email and length checks run as Quasar `rules`. The handler validates again before it saves, and
   * through `validate()` alone it would pass and save the bad chips [sc-20331]. Every other component
   * keeps its handler while invalid, so the first click-away after a correction saves [sc-29284].
   */
  autoSaveOffWhileInvalid?: boolean
}

/** How long the "changes saved" hint stays up. */
const AUTO_SAVE_MESSAGE_MS = 5000

export function useBsComponent (context: BsComponentContext) {
  const { vm, props, emit } = context

  const autoSaveMethod = context.autoSaveMethod ?? 'blur'

  const autoSaveMessage = ref('')
  const errorMessage = ref('')
  const componentState = ref<ComponentState>(ComponentState.Valid)

  /**
   * Make a component always trigger save on change i.e Radio button first click. A ref rather than a
   * context member because BsRadio flips it at runtime.
   */
  const forceTriggerOnChange = ref(false)

  /** Avoid an auto save when initial value is set on the component (mainly BsRadio) */
  const stopSaveOnFirstSet = ref(false)

  /**
   * The value as it was when the field was first shown, so a blur that changed nothing saves nothing.
   * Not a ref: nothing renders it.
   */
  let initialValue: any

  /**
   * The other field a cross-field rule ("passwords must match") flagged, kept so its error can be cleared
   * when this one becomes valid.
   */
  let crossValidatedRef: any

  /**
   * Guards against the same change being reported twice. BsRadio's v-model appears to update twice with
   * the same value; carried over verbatim rather than diagnosed.
   */
  let alreadyChangedValues: Array<Record<number, { from: any, to: any }>> = []

  /**
   * `_uid` in Vue 2; Vue 3 keeps the instance's id on the internal instance as `$.uid`. Read through
   * a function so a spec can still blank it to exercise the no-uid branches. (`vm.$` itself is always
   * there, so it takes no `?.`.)
   */
  const uid = () => (vm.$ as any).uid

  /**
   * What the component currently models — from its own `value` prop if it declares one, else from
   * whichever of the two attribute names the caller used.
   *
   * `modelValue` first, because that is what `v-model` on a `bs-*` component compiles to under Vue 3:
   * `:modelValue` plus `@update:modelValue`. Reading only `$attrs.value` (Vue 2's name, which the
   * bundle's own contract still accepts) meant every v-model binding in the apps -- most of them --
   * looked empty to the component: BsSelect could not find the option matching it, so its display and
   * its "don't offer the option already chosen" both went blank.
   */
  const componentValue = computed(() => {
    const declared = context.value?.()

    if (declared !== void 0) {
      return declared
    }

    // Chosen by which name was bound, not by `??` or `||`: a falsy model (an unchecked toggle, a zero
    // rating, step 0 of a wizard) is a value, and so is `null` — an empty column as the api sends it.
    // `??` turned that `null` into `undefined`, which `onUpdated` reads as "the data has not arrived
    // yet", so a link pasted into an empty field became the initial value and its blur never saved.
    // Vue 2 read the bound `value` and kept the `null`. [sc-28796]
    return 'modelValue' in vm.$attrs ? vm.$attrs.modelValue : vm.$attrs.value
  })

  const hasErrors = computed(() => componentState.value === ComponentState.Invalid)

  const debug = (str: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${uid()}]`, str)
    }
  }

  const isComponentIsValid = (val?: any) =>
    (context.isComponentValid ?? ((v?: any) => v !== null && v !== '' && v !== void 0))(val)

  const validate = (val?: any): boolean => {
    // https://app.shortcut.com/booksprout/story/7858/bs-common-issues-with-login
    if (val && typeof val === 'string') {
      val = val.trim()
    }

    // If we're resetting this component, don't trigger validation
    if (componentState.value === ComponentState.Resetting) {
      componentState.value = ComponentState.Valid
      errorMessage.value = ''
      return true // Return this as valid as the empty state in this instance is valid.
    }

    const isEmpty = isComponentIsValid(val) === false

    // Is required and is empty check
    componentState.value = props.required && isEmpty ? ComponentState.Invalid : ComponentState.Valid
    if (componentState.value === ComponentState.Invalid) {
      errorMessage.value = vm.$tc('system.labels.requiredField')
      return false
    }

    // The value is valid so far — clear any stale error from a previous validation. The email and
    // validation-rule checks below re-set it if the CURRENT value fails. Without this, a value that
    // has since been corrected keeps showing the old error message (the rules only ever SET the
    // error on failure, never clear it on success).
    errorMessage.value = ''

    if (props.validEmail && !emailValidator.validate(val)) {
      componentState.value = ComponentState.Invalid
      errorMessage.value = vm.$tc('system.errors.invalidEmail')
      return false
    }

    if (props.validation !== void 0) {
      // Validate any validation rules attached to the component
      for (const validationRule of props.validation) {
        /**
         * A rule that throws used to take the whole form down silently. `validateComponentTree` runs
         * inside `BsForm.validateForm`, which BsTabStepper awaits and whose rejection it discards, so one
         * unsafe rule — `val.length` against an empty OPTIONAL field, which is `undefined` on a model
         * built in the browser — turned the wizard's next button into a no-op with nothing on screen and
         * nothing in the console. It cost the book wizard its Files step (sc-27875).
         *
         * A throwing rule is now treated as having nothing to say, and reported: a broken rule stops
         * being able to strand the user, and the remaining rules still get their turn.
         */
        let validationResult: ReturnType<ValidationRule>
        try {
          /**
           * Pass in the value of our component so it can use that for validation. An empty value is
           * normalised to '' because the empty check above only returns early for a REQUIRED field — an
           * optional one falls through here with null/undefined, and a rule that measures what it is
           * given (val.length, val.trim()) then throws. '' keeps rules that mean to reject an empty
           * value working, since it is still falsy. [sc-27865, carried from dev]
           *
           * The catch below stays: it contains a throw from any OTHER unsafe rule, which is a different
           * guarantee from not handing this one a null.
           */
          validationResult = validationRule(val || componentValue.value || '')
        } catch (e) {
          console.error(
            `A validation rule on ${vm.$options.name || 'a bs component'} threw; treating the value as valid.`,
            e
          )
          continue
        }

        // If it has a value, it means it FAILED validation
        if (validationResult !== void 0) {
          errorMessage.value = validationResult.label
          componentState.value = ComponentState.Invalid

          // If this validation references something else, it means it wants that field flagged as well (i.e Fields must match)
          if (validationResult.ref !== void 0) {
            // Keep track of the reference to the "other" field we're marking as invalid so it can be cleared later when valid
            crossValidatedRef = validationResult.ref
            crossValidatedRef.componentState = ComponentState.Invalid
            crossValidatedRef.errorMessage = validationResult.label
          }
        } else if (crossValidatedRef !== void 0) {
          // Clear the validation state against the reference component
          crossValidatedRef.componentState = ComponentState.Valid
          crossValidatedRef.errorMessage = ''
        }
      }
    }

    /**
     * The Quasar-shaped `rules`, after the bundle's own `validation` so an existing failure keeps its
     * message. Same containment as above: a rule that throws is treated as having nothing to say
     * rather than being allowed to strand the user.
     *
     * Breaks on the first failure, which is what Quasar shows too.
     */
    if (props.rules !== void 0) {
      for (const rule of props.rules) {
        /**
         * Quasar also takes a rule by NAME - `'date'`, `'email'` - and resolves it against its own
         * pattern table. Those are meaningful to the Quasar component this renders into, which
         * still receives `rules` and applies them, but they are not callable here: calling one
         * threw a TypeError on every validation pass and logged it below. BsDate is the only
         * caller passing one, and its real guard is the `isValidDate` it puts in `validation`.
         *
         * Deliberately skipped rather than resolved through Quasar's `testPattern`: those patterns
         * reject an empty string (`testPattern.date('')` is false), so gating on them here would
         * fail every empty OPTIONAL date field - and `testPattern` is not on Quasar's public entry.
         */
        if (typeof rule !== 'function') {
          continue
        }

        let result: ReturnType<QuasarValidationRule>
        try {
          result = rule(val || componentValue.value || '')
        } catch (e) {
          console.error(
            `A rule on ${vm.$options.name || 'a bs component'} threw; treating the value as valid.`,
            e
          )
          continue
        }

        if (result === false || typeof result === 'string') {
          errorMessage.value = typeof result === 'string' ? result : vm.$tc('system.errors.invalidValue')
          componentState.value = ComponentState.Invalid
          break
        }
      }
    }

    return componentState.value === ComponentState.Valid
  }

  /** `onRefused` runs once a rejected save has put its error on the field. */
  const doAutoSave = (onRefused?: () => void) => {
    if (props.autoSave !== void 0 && componentState.value !== ComponentState.Invalid) {
      (props.autoSave as () => Promise<any>)().then(() => {
        errorMessage.value = ''
        // `translate`, not `vm.$tc`: an auto-save can still be in flight when the field is unmounted —
        // a wizard step changing, a dialog closing — and vue-i18n deletes the instance translators on
        // unmount. See ArcStats.
        autoSaveMessage.value = translate('system.labels.changesSaved')
        componentState.value = ComponentState.AutoSaved
        setTimeout(() => {
          autoSaveMessage.value = ''
          componentState.value = ComponentState.Valid
        }, AUTO_SAVE_MESSAGE_MS)
      }).catch((e: any) => {
        errorMessage.value = e.message
        componentState.value = ComponentState.Invalid
        onRefused?.()
      })
    }
  }

  const doBeforeAutoSave = (val: any) => {
    // do nothing here. Here for when we need to do something in the component on the same event which is
    // auto saving i.e BsChipBox
    context.doBeforeAutoSave?.(val)
  }

  /**
   * Hijacks the component's own event so a save can run on it, then re-emits it so the v-model still works.
   */
  const getOnAutoSave = () => {
    if (!autoSaveMethod) {
      return void 0
    }

    // A component showing an error keeps its handler: the handler validates again before it saves, so an
    // invalid value still never saves, and taking it off meant the first click-away after a correction only
    // cleared the error [sc-29284]. BsChipBox is the exception; see `autoSaveOffWhileInvalid`.
    if (componentState.value === ComponentState.Invalid && context.autoSaveOffWhileInvalid === true) {
      return void 0
    }

    return {
      [autoSaveMethod]: (val: any) => {
        doBeforeAutoSave(val)

        // Validate the component.
        // If not valid, emit the autoSaveMethod anyway as other code might need it to bubble.
        validate(componentValue.value)

        if (componentState.value === ComponentState.Invalid) {
          emitModel(autoSaveMethod, val)
          return void 0
        }

        componentState.value = ComponentState.AutoSaving

        // Set some defaults
        let value = componentValue.value
        let ignoreCheck = false

        // A checkbox or a chip box: the event is the change. See `autoSaveAlways`.
        if (context.autoSaveAlways === true) {
          value = val
          // Checkbox will always have changed as it was clicked.
          ignoreCheck = true
        }

        // Emit the event we've just hijacked so the component works properly.
        // i.e input will stop the v-model bind from working.
        emitModel(autoSaveMethod, val)

        // We don't want to trigger an auto save on this component if nothing is actually happening.
        if (initialValue !== value || ignoreCheck === true) {
          // Update the intial value so it can auto save again on next change.
          const previousValue = initialValue
          initialValue = value
          // Do auto save. A refused save is not a save: put the initial value back — unless a newer value has
          // gone out since — so the next event tries again. Otherwise that event found nothing changed, and
          // its validation had already cleared the server's error [sc-29284].
          doAutoSave(() => {
            if (initialValue === value) {
              initialValue = previousValue
            }
          })
        }

        return
      }
    }
  }

  /**
   * Renamed from `__renderAutoSaveMessage`: Vue does not proxy setup keys beginning with `_` onto the
   * instance, and BsCheckbox and BsRadio both call this from their own render.
   */
  const renderAutoSaveMessage = () => ({
    hint: () => h('span', autoSaveMessage.value)
  })

  /** Renamed from `__renderErrorSlot`, for the same reason as `renderAutoSaveMessage` above. */
  const renderErrorSlot = () => {
    // If we're trying to put the error message in a different place,
    // do it and don't show the error slot on the component.
    if (props.validationResultTarget === void 0) {
      return h('div', {
        class: 'row nowrap bs-par-s-i'
      }, [
        h(QIcon, {
          class: 'on-left',
          name: 'app:error'
        }),
        h('span', {
          class: 'col'
        }, errorMessage.value)
      ])
    } else {
      props.validationResultTarget.innerText = errorMessage.value
    }

    return void 0
  }

  /**
   * A parent's handlers, under the plain names this bundle uses internally.
   *
   * Vue 3 has no `$listeners`: they arrive in `$attrs` as `onClick`, `onInput` and so on. The bundle's
   * render functions hand this map to `quasarHandlers()`, which maps it back, so the internal convention
   * is unchanged and only the source moves.
   *
   * Re-applying them is safe even though Vue 3 ALSO falls handlers through to the root element by
   * itself: `mergeProps` dedupes an identical function reference, so a parent's onClick fires once.
   * Verified rather than assumed -- a wrapped handler would double-fire, which is why the wrapping
   * below replaces the inherited entry rather than adding to it.
   */
  const inheritedListeners = (): Record<string, any> => Object.fromEntries(
    Object.entries(vm.$attrs)
      .filter(([key, value]) => /^on[A-Z]/.test(key) && typeof value === 'function')
      .map(([key, value]) => [key[2].toLowerCase() + key.slice(3), value])
  )

  /**
   * The bundle's own event, plus Vue 3's model event when they are the same thing.
   *
   * A `bs-*` component's contract is `value` in and `input` out, and several hundred templates bind
   * `@input` on one. But `v-model="x"` on a component compiles to `:modelValue` and
   * `@update:modelValue` under Vue 3 -- so emitting only `input` left every v-model in the apps
   * one-directional: the handler ran, the bound variable never changed. The sort control on the books
   * list reloaded the list on each pick and always with the previous sort, which is how it was found.
   *
   * Both are emitted rather than renaming, so the `@input` bindings keep working.
   *
   * ORDER MATTERS, and it is the order Vue 2 had. There, `v-model` and `@input` on the same component
   * were two handlers for one `input` event and the model setter ran first — so a handler like
   * `@input="filterBooks"` saw the NEW value. Emitting `input` first here reversed that: the books list
   * reloaded on every sort change and always with the previous sort, because `filterBooks` read the
   * bound ref before the setter had touched it.
   */
  const emitModel = (event: string, payload: any) => {
    if (event === 'input') {
      emit('update:modelValue', payload)
    }

    emit(event, payload)
  }

  const getListeners = (additional?: any) => {
    let listeners = {
      ...inheritedListeners(),
      ...additional
    }

    /** Validate (unless told not to) and hand the new value to the caller under both names. */
    const emitModelEvent = (e: any) => {
      if (!props.lazyValidation) {
        validate(e)
      }

      emitModel('input', e)
    }

    // getOnAutoSave will trigger its own validation
    if (props.autoSave === void 0) {
      listeners = {
        ...listeners,
        input: emitModelEvent
      }
    } else {
      const onAutoSave = getOnAutoSave()

      listeners = {
        ...listeners,
        ...onAutoSave
      }

      /**
       * The model event still has to come from US.
       *
       * With `auto-save` set, the auto-save handler is the only one this attached — and for a component
       * whose `autoSaveMethod` is not `input` (BsRadio saves on blur), that left nothing emitting the
       * value at all. It used to work by accident: the caller's own `@update:modelValue` sat in
       * `$attrs` and `inheritedListeners` forwarded it to the Quasar component inside. Declaring
       * `emits` (which is what stops a caller's `@input` ALSO being bound as a native listener) takes
       * it out of `$attrs`, and the arc wizard's participation radio went dead — clicking an option
       * changed nothing and saved nothing.
       *
       * Skipped when something already handles `input`: the auto-save handler when its method IS
       * `input`, or the component's own handler — BsInput trims and normalises, BsChipBox flattens a
       * csv, BsEditor strips inline styles — each of which emits the model itself through `emitModel`.
       */
      if (onAutoSave?.input === void 0 && additional?.input === void 0) {
        listeners = {
          ...listeners,
          input: emitModelEvent
        }
      }
    }

    return listeners
  }

  /**
   * The slots to hand a Quasar component as `h()`'s third argument.
   *
   * `$slots` is gone -- Vue 3 merged both collections into `$slots` and made every entry a
   * function -- and `h` no longer needs passing in, so this loses its first parameter.
   */
  const getSlots = (addSlots?: any) => {
    let slots = {
      ...vm.$slots,
      ...addSlots
    }

    if (autoSaveMessage.value !== '') {
      slots = {
        ...slots,
        ...renderAutoSaveMessage()
      }
    }

    if (componentState.value === ComponentState.Invalid) {
      slots = {
        ...slots,
        error: () => renderErrorSlot()
      }
    }

    return slots
  }

  const getAttributes = (additional?: any, skipDataModel = false) => {
    if (!skipDataModel && props.dataModel !== void 0) {
      additional = { ...additional, 'data-model': props.dataModel }
    }

    /**
     * `class` and `style` are dropped here too. Callers pass `{ ...this.$attrs }` in as `additional`,
     * which put them back after the render had set its own -- so a component's class array (`bs-btn`
     * and friends) was replaced by whatever the caller happened to bind. Vue 3 merges those two
     * attributes from fallthrough with the render's, which is the behaviour wanted.
     */
    const { class: _class, style: _style, ...rest } = { ...additional } as Record<string, any>
    void _class
    void _style

    return {
      placeholder: vm.$attrs.placeholder,
      /**
       * `|| null`, not a bare boolean: Vue 3 renders `false` as the string "false" on a plain
       * element instead of dropping the attribute the way Vue 2 did. `null` removes it, and is
       * also what Quasar's own `error` prop defaults to, so components see no change.
       */
      error: errorMessage.value !== '' || null,
      ...rest
    }
  }

  const getValidationProps = (additional?: any) => ({
    ...additional,
    color: hasErrors.value ? 'imp-bs-r' : additional?.color || vm.$attrs?.color,
    error: hasErrors.value
  })

  /**
   * What the parent bound on this component, to be forwarded to the Quasar component inside it.
   *
   * Vue 2 read this off `$vnode.data.props`, the declared props the parent passed. Vue 3 has no
   * `$vnode` (the instance's own vnode is `$.vnode`) and no `data` split: `vnode.props` is one flat
   * object holding declared props, plain attributes AND event listeners as `onXxx` keys.
   *
   * The listeners are dropped, which is what keeps this a translation rather than a change: in Vue 2
   * they lived in `data.on` / `$listeners`, so they were never part of what this forwarded. Passing
   * them on would attach the parent's handlers to the inner component as well as the outer one.
   */
  const propsPassedToUs = (host: any) => {
    // The one caller passes `vm`, which always has its instance and vnode; `props` alone can be null.
    const passed = host.$.vnode.props

    if (!passed) {
      return {}
    }

    /**
     * `ref`, `key`, `class` and `style` are dropped along with the listeners: they belong to the vnode
     * the PARENT created, not to the component inside this one, and Vue 3 already merges class and
     * style from attribute fallthrough with whatever the render sets.
     *
     * Forwarding them replaced what the render had set. `ref` overwrote the bundle's own template ref
     * with the parent's -- test-utils attaches one to every mount, so `this.bsChipBox` and friends came
     * back null -- and `class` replaced the component's own class array, so `bs-btn` vanished from
     * every button rendered through another bundle component.
     */
    const DROP = new Set(['ref', 'key', 'class', 'style'])

    /**
     * The host's OWN declared props go too, `value` excepted.
     *
     * In Vue 2 this read `$vnode.data.props`, which only a hand-written render populated -- a template
     * parent's `text="..."` binding went to `data.attrs` and was then extracted into propsData, so it
     * never appeared here. Vue 3 has one flat bag holding both, so without this filter a component's
     * own props (`text`, `tkey`, `autoSave`...) were forwarded to the Quasar component inside and
     * rendered as stray DOM attributes. `value` stays because that IS what the inner control models.
     *
     * Both spellings, because the vnode bag is keyed the way the TEMPLATE wrote it. A parent binding
     * `:auto-save="..."` puts `auto-save` in there, which never matched the declared `autoSave` --
     * so the two multi-word props in the bundle, `autoSave` and `limitedToolbar`, went straight
     * through this filter and ended up as attributes on the field: `auto-save="() => crud.autoSave(…)"`,
     * the whole function source, in the page.
     */
    const own = new Set(
      Object.keys(props)
        .filter(key => key !== 'value')
        .flatMap(key => [ key, key.replace(/[A-Z]/g, letter => '-' + letter.toLowerCase()) ])
    )

    return withoutTestHooks(Object.fromEntries(
      Object.entries(passed).filter(([key]) =>
        !/^on[A-Z]/.test(key) && !DROP.has(key) && !own.has(key))
    ))
  }

  /**
   * Speak Quasar 2's v-model to the component inside, while the bundle keeps speaking `value`/`input`
   * to its own callers.
   *
   * Quasar 2 renamed the model prop to `modelValue` and its event to `update:modelValue`. Every app
   * template binds `value` (and `v-model`) on a `bs-*` component, and every spec asserts it, so
   * translating here -- at the boundary the bundle exists to be -- keeps that contract intact instead
   * of rewriting several hundred call sites for a rename Quasar made internally.
   */
  const toQuasar2 = (bag: Record<string, any>) => {
    const { value, onInput, ...rest } = bag

    /**
     * A `modelValue` already in the bag WINS over the incoming `value`. BsRating passes its rounded
     * value as `modelValue` while the caller's raw `value` is still in `$attrs`, and translating
     * unconditionally put the raw one back -- QRating got 3.9 instead of 4.
     */
    return {
      ...rest,
      ...(value !== void 0 && rest.modelValue === void 0 ? { modelValue: value } : {}),
      ...(onInput !== void 0 && rest['onUpdate:modelValue'] === void 0 ? { ['onUpdate:modelValue']: onInput } : {})
    }
  }

  const getRenderProps = (additional?: any) => {
    /**
     * A COPY, because `$attrs` is readonly in Vue 3 -- writing the translated label straight into it
     * warned ("Set operation on key label failed: target is readonly") and did nothing, so a `tkey`
     * label silently stopped appearing.
     */
    /**
     * `class` and `style` are left out. Vue 3 MERGES those two from attribute fallthrough with what the
     * render sets, which is what you want -- a caller's class adds to the component's own. Re-spreading
     * them here instead REPLACED the component's class array, so `bs-btn` and the caller's class both
     * disappeared and only Quasar's own classes were left on the element.
     */
    const { class: _class, style: _style, ...attrs }: Record<string, any> =
      withoutTestHooks({ ...(vm.$attrs ?? {}) })
    void _class
    void _style

    if (props.tkey !== void 0 && props.tkey !== '') {
      attrs.label = vm.$tc(props.tkey)
    }

    return toQuasar2({
      ...propsPassedToUs(vm),
      ...attrs,
      ...getValidationProps(additional)
    })
  }

  const getClasses = (additional?: any) => [{
    'bs-auto-saving': componentState.value === ComponentState.AutoSaved,
    ...additional
  }]

  /**
   * Walks every component under the parent, depth first, and reports whether the form is valid.
   *
   * `mutate` is the Vue 3 addition and it is not cosmetic. `validate()` WRITES `componentState` and
   * `errorMessage`, which is right when a submit or a blur asks the form to validate itself -- the
   * fields are supposed to light up. It is fatal from inside a render: `isFormValid` is read while
   * BsBtn renders, the walk mutated the state that render depends on, the computed invalidated, and
   * it re-rendered forever. Vue 2 got away with it because its computed cached for the whole tick.
   *
   * So a render-time read (`mutate: false`) asks each component the same question through
   * `isComponentIsValid`, which is pure. An interactive validation (`mutate: true`) still calls
   * `validate()` and still shows the errors.
   *
   * The recursive call comes FIRST in the `&&` on purpose — short-circuiting would skip validating
   * the rest of the form.
   */
  const walkComponentTree = (components: any, mutate: boolean, seen = new Set<any>()): boolean => {
    let isValid = true

    for (const component of components) {
      // A guard against a cycle in the tree rather than an expected case: revisiting a component
      // would also re-run its validation, and with `mutate` on that is a write.
      if (seen.has(component)) {
        continue
      }
      seen.add(component)

      const children = childComponents(component)
      if (children.length > 0) {
        // NOTE: This must be this way around because if not and isValid === false, it will skip
        // the walk meaning it won't validate all the form components under the form. Took too long
        // to figure this out ...
        isValid = walkComponentTree(children, mutate, seen) && isValid
      }

      /**
       * The component's own resolved value, not `$attrs.value`.
       *
       * Vue 2's fallback read the v-model expression off `$vnode.data.model`, which Vue 3 does not
       * record — and `$attrs.value` alone is the wrong name for every caller that binds `v-model`,
       * which sends `modelValue`. Validating the missing name marked filled fields invalid: the arc
       * wizard's team step failed its form validation with a team plainly chosen in it, and the wizard
       * simply refused to advance.
       */
      const value = component.componentValue ?? component.$attrs?.modelValue ?? component.$attrs?.value

      if (mutate) {
        if (component.validate === void 0) {
          continue // Nothing to ask; say it's valid
        }

        // The call comes FIRST, as it did before: `isValid && component.validate(...)` skips the
        // validation of everything after the first failure, so those fields never light up.
        const isComponentValid = component.validate(value)
        isValid = isValid && isComponentValid
        continue
      }

      /**
       * The read-only equivalent of `validate()`, and it has to mirror its FIRST rule: a field is
       * invalid only when it is required and empty. Asking `isComponentIsValid` alone reported every
       * optional empty field as invalid, which disabled submit buttons on untouched forms.
       */
      if (component.required !== true || component.isComponentIsValid === void 0) {
        continue
      }

      const isComponentValid = component.isComponentIsValid(value) === true
      isValid = isValid && isComponentValid
    }

    return isValid
  }

  const validateComponentTree = (components: any): boolean => walkComponentTree(components, true)

  /**
   * Was returning the NEGATION, so a form whose fields were all valid reported itself invalid.
   *
   * Read-only, which is what makes it safe to read while rendering — see `walkComponentTree`.
   */
  const isFormValid = computed(() => walkComponentTree(childComponents(vm.$parent), false))

  const reset = () => {
    componentState.value = ComponentState.Resetting
    emit('input', initialValue)
  }

  /**
   * Some components will want to trigger the save the moment something changes i.e BsSelect.
   * When the trigger-on-change prop is passed then this will watch for changes and save.
   */
  watch(componentValue, (to: any, from: any) => {
    // this covers duplicate calls to autoSave
    // due to the values being the same
    // no idea how it happens, but seems to only happen with BsRadio component
    // maybe v-model gets updated twice with the same value?
    const alreadyChanged = alreadyChangedValues.find(obj => {
      return obj[uid()]?.from === from && obj[uid()]?.to === to
    })

    if (alreadyChanged) {
      alreadyChangedValues = []
      return
    }

    // Validate when any changes happen to the data as this will clear the state if another
    // method / field has updated this value.
    if (!props.lazyValidation) {
      validate(to)
    }

    if (
      (!forceTriggerOnChange.value && !props.triggerOnChange) || props.autoSave === void 0 ||
      from === to
    ) {
      return
    }

    /**
     * Components like BsRadio will trigger an @input event as soon as a value has been assigned to them.
     * This isn't good as it'll trigger an auto save as soon as that component is assigned a value.
     * We set the stopSaveOnFirstSet = true against that component so that it doesn't do the initial save
     * when the from value === void 0 (no value set already).
     */
    if (!stopSaveOnFirstSet.value || from !== void 0) {
      if (uid()) {
        alreadyChangedValues.push({
          [uid()]: { from, to }
        })
      }

      doAutoSave()
    }
  })

  watch(errorMessage, (to: string) => {
    // If the error message is cleared, also clear the content of our validation result target
    if (!to && props.validationResultTarget) {
      props.validationResultTarget.innerText = ''
    }
  })

  onMounted(() => {
    initialValue = componentValue.value
  })

  onUpdated(() => {
    // If by this point we have a value but didn't on mounted, that means it took a moment to
    // get the data. Update that now so that we have a value set for the "blur" event so it
    // won't trigger an auto save for a value that hasn't changed.
    if (initialValue === void 0 && componentValue.value !== void 0) {
      initialValue = componentValue.value
    }
  })

  return {
    autoSaveMessage,
    errorMessage,
    componentState,
    forceTriggerOnChange,
    stopSaveOnFirstSet,
    componentValue,
    hasErrors,
    isFormValid,
    debug,
    slot: (host: any, slotName: any, otherwise?: any) => slot(host, slotName, otherwise),
    isComponentIsValid,
    validate,
    validateComponentTree,
    doAutoSave,
    doBeforeAutoSave,
    getOnAutoSave,
    getListeners,
    getSlots,
    getAttributes,
    getValidationProps,
    getRenderProps,
    getClasses,
    reset,
    renderAutoSaveMessage,
    renderErrorSlot
  }
}
